import React, { PropsWithChildren, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import collect from 'collect.js';
import toast from 'react-hot-toast';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { AlertTriangle, ChevronRight, RotateCw } from 'lucide-react';
import { Elements, ExpressCheckoutElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type {
  StripeElements,
  StripeError,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementOptions,
  StripeExpressCheckoutElementReadyEvent,
  StripePaymentElementChangeEvent,
  StripePaymentElementOptions,
} from '@stripe/stripe-js';
import { StoreInterface } from '../../../store/types';
import {
  ORDER_METHODS,
  OrderType,
  PaymentGateway,
  PaymentMethod as PaymentMethodEnum,
  PaymentStatus,
  PaypalVariant,
} from '../../../services/exports/Constants';
import { CompanyDetailsInterface, PaymentOptionPropertiesInterface } from '../../../services/exports/Interfaces';
import { chargesStripeDestination } from '../../../utils/stripe';
import usePaymentFlow from '../../../hooks/payment/usePaymentFlow';
import { usePaymentCoordinator } from '../../../hooks/payment/usePaymentCoordinator';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import useOrderFlow from '../../../hooks/order/useOrderFlow';
import useDeliveryThreshold from '../../../hooks/order/useDeliveryThreshold';
import useCheckoutThreshold from '../../../hooks/order/useCheckoutThreshold';
import useBillingInfo from '../../../hooks/payment/useBillingInfo';
import useDeepRef from '../../../hooks/utility/useDeepRef';
import useCheckout, {
  ConfirmPaymentFunction,
  GenerateOrderReturnUrlFunction,
  PaymentReturnUrlPlaceholder,
  useGenerateReturnUrl,
} from '../../../hooks/payment/useCheckout';
import { CheckoutFunction } from '../../../hooks/payment/useOrderCheckout';
import PaymentMethod from '../payment-methods/PaymentMethod';
import PaymentMethodBrandIcons from '../payment-methods/PaymentMethodBrandIcons';
import { Variant } from '../payment-methods/PaymentMethodOption';
import { handleStripeNextAction } from '../buttons/types';
import paymentLog from '../../../services/helpers/PaymentLogger';
import { useStripeElementsMounted, useStripeElementsSession } from '../StripeElementsMounted';
import { StripeWallet, paymentMethodFromStripeType } from '../../../utils/payment-options-visibility';
import useStripeElementsExclusions from '../../../hooks/payment/useStripeElementsExclusions';
import PaymentMethodsSkeleton from '../PaymentMethodsSkeleton';
import retireExpressCheckoutElement from './retireExpressCheckoutElement';
import { isInAppBrowser } from '../../../utils/in-app-browser';

type Props = {
  loading: boolean;
  filteredPaymentOpts: PaymentOptionPropertiesInterface[];
};

/**
 * The Stripe checkout, driven by the restaurant's own account:
 * the Express Checkout Element for the wallets, the Payment Element accordion for whatever else the
 * account can take, and — drawn by this shop in the same look — the card on file above it, cash
 * below it and PayPal in the collapsed "more methods" accordion, since Stripe has no direct charges
 * for PayPal and it stays on the platform account. A restaurant on destination charges gets PayPal
 * there only as PayPal-direct, never through Stripe. Inside the native app the wallets keep their
 * own rows and the app's bridge, and no Express Checkout Element is mounted.
 *
 * Mounted inside the deferred <Elements> the PaymentCoordinator opens where the restaurant's charges
 * are made: its connected account for direct charges, the partner's platform with the restaurant as
 * on_behalf_of for destination charges. The intent is created at order time (see StripePayButton and
 * the Express Checkout confirm below).
 */
export default function StripeElementsPaymentMethods(props: Props) {
  const elementsMounted = useStripeElementsMounted();
  const { pending } = useStripeElementsSession();

  if (pending) {
    return <StripeElementsPaymentMethodsPending {...props} />;
  }

  if (!elementsMounted) {
    return <StripeElementsPaymentMethodsWithoutElements {...props} />;
  }

  return <StripeElementsPaymentMethodsContent {...props} />;
}

/**
 * A logged-in customer's Elements wait for their CustomerSession (usually asked for on the checkout step
 * already): the block's placeholder, not the cash-only fallback, and nothing worth a log line.
 */
function StripeElementsPaymentMethodsPending({ filteredPaymentOpts }: Props) {
  useHoldsPayButton(true);

  return (
    <div data-test="stripe-elements-payment-methods" data-elements="pending">
      <StripeElementsSkeleton filteredPaymentOpts={filteredPaymentOpts} />
    </div>
  );
}

/** How long the payment block waits for Stripe before it shows what it has. */
export const STRIPE_ELEMENTS_REVEAL_TIMEOUT_MS = 3_000;

/**
 * How long the places Stripe has not drawn yet are held after that. Past it Stripe.js is taken to be
 * blocked or stuck (no ready, no load error): the block stays as it is, cash and PayPal included,
 * without placeholders pulsing forever.
 */
export const STRIPE_ELEMENTS_GIVE_UP_MS = 10_000;

/**
 * The Pay button waits while the block shows its placeholder (StripeElementsSession.blockPending),
 * and stops waiting when the block goes away.
 */
function useHoldsPayButton(pending: boolean) {
  const { setBlockPending } = useStripeElementsSession();

  useEffect(() => {
    setBlockPending(pending);
  }, [pending]);

  useEffect(() => () => setBlockPending(false), []);
}

const WALLETS_SEEN_KEY = 'stripe-elements-wallets';

/**
 * How many wallet buttons the Express Checkout Element showed at this restaurant earlier in the visit
 * (they depend on the browser and the account, neither of which changes within it), so the placeholder
 * draws as many as will appear. Unknown at first: two, the usual on a phone or in Chrome.
 */
function walletsSeen(companyId: number | undefined): number | null {
  try {
    const seen = JSON.parse(window.sessionStorage.getItem(WALLETS_SEEN_KEY) ?? '{}')[String(companyId)];

    return typeof seen === 'number' ? seen : null;
  } catch {
    return null;
  }
}

function rememberWalletsSeen(companyId: number | undefined, count: number): void {
  try {
    const seen = JSON.parse(window.sessionStorage.getItem(WALLETS_SEEN_KEY) ?? '{}');

    window.sessionStorage.setItem(WALLETS_SEEN_KEY, JSON.stringify({ ...seen, [String(companyId)]: count }));
  } catch {
    // A private window or blocked storage: the placeholder keeps its two.
  }
}

/**
 * Whether the PayPal row is drawn. Under destination charges PayPal through Stripe would be a
 * platform charge with no restaurant behind it, so only PayPal-direct is offered there.
 */
function offersPaypal(
  company: CompanyDetailsInterface | null | undefined,
  paypalVariant: PaypalVariant | null,
): boolean {
  return paypalVariant !== null && !(paypalVariant === PaypalVariant.ViaStripe && chargesStripeDestination(company));
}

/**
 * Whether the order's amount is known yet: the draft has come back (or the gift-card basket, which has
 * no draft). Before that a zero total means "not known", not "free".
 */
function useAmountKnown(): boolean {
  const { data } = useOrderFlow();
  const { isGiftCardCheckout } = usePaymentFlow();

  return isGiftCardCheckout || Boolean(data?.id);
}

/** A guess at the Payment Element's rows before Stripe says: the card and one more is the usual. */
const ESTIMATED_ELEMENT_ROWS = 2;

type SkeletonShape = { rows: number; withExpressCheckout: boolean; wallets: number; withMoreMethods: boolean };

/** What the block will roughly look like: the wallets' buttons, every row, the "more methods" heading. */
function useStripeElementsSkeletonShape(filteredPaymentOpts: PaymentOptionPropertiesInterface[]): SkeletonShape {
  const { isMobileApp } = useMobileApp();
  const { basketTotal } = usePaymentCoordinator();
  const { findSavedPaymentMethod, paymentOptions, paypalVariant } = usePaymentFlow();
  const amountKnown = useAmountKnown();
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const seenWallets = walletsSeen(company?.id);

  return useMemo(() => {
    const ownRows = filteredPaymentOpts.filter(
      (item) =>
        item.payment_method_id === PaymentMethodEnum.Cash ||
        (isMobileApp && [PaymentMethodEnum.ApplePay, PaymentMethodEnum.GooglePay].includes(item.payment_method_id)),
    ).length;

    return {
      rows: ownRows + (findSavedPaymentMethod(PaymentMethodEnum.Card) ? 1 : 0) + ESTIMATED_ELEMENT_ROWS,
      withExpressCheckout: !isMobileApp && (basketTotal > 0 || !amountKnown) && seenWallets !== 0,
      wallets: seenWallets ?? 2,
      withMoreMethods:
        offersPaypal(company, paypalVariant) &&
        filteredPaymentOpts.some((item) => item.payment_method_id === PaymentMethodEnum.Paypal),
    };
  }, [
    filteredPaymentOpts,
    isMobileApp,
    basketTotal > 0,
    amountKnown,
    seenWallets,
    paymentOptions,
    paypalVariant,
    company?.stripe_charge_model,
  ]);
}

/**
 * One placeholder for the whole payment block, sized like it, while Stripe draws the real one. Also
 * what the payment section shows before the payment set-up is ready (PaymentInfoSection).
 */
export function StripeElementsSkeleton({ filteredPaymentOpts }: Pick<Props, 'filteredPaymentOpts'>) {
  const { rows, withExpressCheckout, wallets, withMoreMethods } = useStripeElementsSkeletonShape(filteredPaymentOpts);

  return (
    <PaymentMethodsSkeleton
      rows={rows}
      withWallets={withExpressCheckout}
      wallets={wallets}
      withMoreMethods={withMoreMethods}
      testId="stripe-elements-skeleton"
    />
  );
}

/**
 * The coordinator opens no <Elements> when Stripe.js may not load: a live key on a plain-HTTP page
 * (a custom domain before its certificate, SHOP-WEB-2R) or a provider without a key. The Stripe
 * hooks would throw into the ErrorBoundary there, so checkout keeps the one row that needs no
 * Stripe, cash, and the pay button stays inert for anything else.
 */
function StripeElementsPaymentMethodsWithoutElements({ loading, filteredPaymentOpts }: Props) {
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { isPaymentProcessing } = usePaymentCoordinator();
  const { selectedPaymentMethod, setSelectedPaymentMethod } = usePaymentFlow();

  const cashOption = useMemo(
    () => collect(filteredPaymentOpts).firstWhere('payment_method_id', PaymentMethodEnum.Cash),
    [filteredPaymentOpts],
  );

  // Once per mount, as the pay button's own report: the reason does not change while the page lives.
  useEffect(() => {
    paymentLog.warn('stripe', 'payment_element_without_elements', { company_id: company?.id });
  }, []);

  return (
    <div className="flex flex-col gap-sm" data-test="stripe-elements-payment-methods" data-elements="false">
      {Boolean(cashOption) && (
        <PaymentMethod
          id={PaymentMethodEnum.Cash}
          name={cashOption.name}
          extra={<PaymentMethodBrandIcons {...cashOption} className={brandIcon} />}
          isLastItem
          isSelected={selectedPaymentMethod === PaymentMethodEnum.Cash}
          isDisabled={loading || isPaymentProcessing}
          onClick={() => setSelectedPaymentMethod(PaymentMethodEnum.Cash)}
          variant={Variant.StripeElements}
          key={`payment-method-option-${PaymentMethodEnum.Cash}`}
        />
      )}
    </div>
  );
}

function StripeElementsPaymentMethodsContent({ loading, filteredPaymentOpts }: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:Sections:PaymentInfoSection' });
  const { t: tCoordinator } = useTranslation(undefined, { keyPrefix: 'Components:Payment:PaymentCoordinator' });
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { cached_order } = useSelector((state: StoreInterface) => state.order);

  const { isMobileApp } = useMobileApp();
  const elements = useElements();
  const { stripeFormat, billingPhoneNumber } = useBillingInfo();
  const { isPaymentProcessing } = usePaymentCoordinator();
  const {
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    isSavedCardSelected,
    selectSavedCard,
    selectStripeElementType,
    stripeElementType,
    paymentOptions,
    findSavedPaymentMethod,
    paypalVariant,
    isGiftCardCheckout,
  } = usePaymentFlow();

  const savedCard = useMemo(() => findSavedPaymentMethod(PaymentMethodEnum.Card), [paymentOptions]);
  const cashOption = useMemo(
    () => collect(filteredPaymentOpts).firstWhere('payment_method_id', PaymentMethodEnum.Cash),
    [filteredPaymentOpts],
  );
  const paypalOption = useMemo(
    () => collect(filteredPaymentOpts).firstWhere('payment_method_id', PaymentMethodEnum.Paypal),
    [filteredPaymentOpts],
  );
  // The wallets the native app confirms through its bridge; on the web they are the Express Checkout Element's.
  const nativeWalletOptions = useMemo(
    () =>
      isMobileApp
        ? filteredPaymentOpts.filter((item) =>
            [PaymentMethodEnum.ApplePay, PaymentMethodEnum.GooglePay].includes(item.payment_method_id),
          )
        : [],
    [filteredPaymentOpts, isMobileApp],
  );
  const { hiddenWallets } = useStripeElementsExclusions(isGiftCardCheckout);
  const reportLoadError = useElementLoadErrorReport();
  const { stripeJs, retryElements, elementsAttempt } = useStripeElementsSession();
  const stripeJsRef = useDeepRef(stripeJs);

  const disabled = loading || isPaymentProcessing;

  // Out of the column until Stripe has drawn it, so an Element with nothing to show (still loading,
  // every method hidden, or refused by Stripe) adds no empty gap above the rows below it.
  const [elementReady, setElementReady] = useState(false);
  // Stripe destroys an Element it could not load, and an options update on it then throws
  // (SHOP-WEB-58): it leaves the tree, and the notice with its retry takes its place.
  const [elementLoadFailed, setElementLoadFailed] = useState(false);

  // The block appears at once, not in stages (our rows, then the card, then the wallets pushing it all
  // down): one placeholder until the Payment Element has answered and the Express Checkout Element has
  // settled, or until Stripe has had long enough. The real block is laid out underneath meanwhile, so
  // both iframes load side by side.
  const [elementSettled, setElementSettled] = useState(false);
  const [expressSettled, setExpressSettled] = useState(isMobileApp);
  const [expressAvailable, setExpressAvailable] = useState(false);
  const [revealTimedOut, setRevealTimedOut] = useState(false);
  const revealed = (elementSettled && expressSettled) || revealTimedOut;
  const revealedRef = useDeepRef(revealed);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!revealedRef.current) {
        paymentLog.info('stripe', 'payment_block_reveal_timed_out', {
          waited_ms: STRIPE_ELEMENTS_REVEAL_TIMEOUT_MS,
        });
      }

      setRevealTimedOut(true);
    }, STRIPE_ELEMENTS_REVEAL_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, []);

  // Stripe.js blocked, or its session hanging: no ready and no load error will ever come.
  const settledRef = useDeepRef({ element: elementSettled, express: expressSettled });
  const mountedAtRef = useRef(Date.now());
  const gaveUpOnElementRef = useRef(false);
  const pageWasHiddenRef = useRef(typeof document !== 'undefined' && document.hidden);
  // This block goes away because the Elements are being re-created, not because the customer left.
  const recreatingRef = useRef(false);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        pageWasHiddenRef.current = true;
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!settledRef.current.element) {
        // SHOP-WEB-47: a slow Element (iOS Safari, a backgrounded tab), a blocker, or an in-app browser
        // all land here, and most customers then pay with a row of ours. Only a customer left with no
        // way to pay is an issue (reported when they leave, below); a late ready is logged when it comes.
        gaveUpOnElementRef.current = true;
        paymentLog.warn('stripe', 'payment_element_never_ready', {
          waited_ms: STRIPE_ELEMENTS_GIVE_UP_MS,
          stripe_js: stripeJsRef.current,
          ...(elementsAttempt > 0 && { elements_attempt: elementsAttempt }),
          ...(isInAppBrowser() && { in_app_browser: true }),
          ...(pageWasHiddenRef.current && { page_hidden: true }),
        });

        // A customer with nothing else to pay with gets one fresh start of the Elements on their own:
        // the Element's session hanging is usually a request Stripe never answered, not a refusal.
        if (
          elementsAttempt === 0 &&
          !strandedRef.current.hasOtherWayToPay &&
          !document.hidden &&
          retryElements('auto')
        ) {
          recreatingRef.current = true;

          return;
        }

        setElementSettled(true);
      }

      if (!settledRef.current.express) {
        paymentLog.info('stripe', 'express_checkout_never_settled', { waited_ms: STRIPE_ELEMENTS_GIVE_UP_MS });
        setExpressSettled(true);
      }
    }, STRIPE_ELEMENTS_GIVE_UP_MS);

    return () => clearTimeout(timer);
  }, []);

  // Stripe.js itself never came (blocked, or the network dropped it): nothing will draw, so the
  // block says so now, with its retry, rather than after the give-up.
  useEffect(() => {
    if (stripeJs !== 'failed') {
      return;
    }

    gaveUpOnElementRef.current = true;
    setElementSettled(true);
    setExpressSettled(true);
  }, [stripeJs]);

  useHoldsPayButton(!revealed);

  const onExpressSettled = useCallback((available: boolean) => {
    setExpressAvailable(available);
    setExpressSettled(true);
  }, []);

  // One of the rows this shop draws is taken: the Element's open item folds, so one radio is lit.
  const collapseElement = useCallback(() => {
    elements?.getElement('payment')?.collapse();
  }, [elements]);

  const selectRow = (method: PaymentMethodEnum) => {
    collapseElement();
    setSelectedPaymentMethod(method);
  };

  const onElementChange = (event: StripePaymentElementChangeEvent) => {
    // A fold we asked for (a row outside the Element was taken) is not a choice.
    if (event.collapsed) {
      return;
    }

    selectStripeElementType(event.value.type);
  };

  // One of the rows this shop draws holds the selection: the card on file, cash, PayPal, a native wallet.
  const isOwnRowSelected =
    isSavedCardSelected ||
    selectedPaymentMethod === PaymentMethodEnum.Cash ||
    selectedPaymentMethod === PaymentMethodEnum.Paypal ||
    nativeWalletOptions.some((item) => item.payment_method_id === selectedPaymentMethod);

  // A wallet of the Express Checkout Element's is no row of the Element's, even with the Element's
  // last row still lit underneath (StripePayButton points to the wallet's own button meanwhile).
  const isExpressWalletSelected =
    !isMobileApp && isExpressCheckoutWalletSelection(selectedPaymentMethod, stripeElementType);

  const isElementSelected = selectedPaymentMethod !== null && !isOwnRowSelected && !isExpressWalletSelected;

  // Whether the customer has anything else to pay with when the Element is given up on.
  const hasOtherWayToPay =
    expressAvailable ||
    Boolean(savedCard) ||
    Boolean(cashOption) ||
    (Boolean(paypalOption) && offersPaypal(company, paypalVariant)) ||
    nativeWalletOptions.length > 0;

  const strandedRef = useDeepRef({ elementReady, hasOtherWayToPay });

  useEffect(() => {
    let reported = false;

    const reportIfStranded = () => {
      const { elementReady: ready, hasOtherWayToPay: otherWay } = strandedRef.current;

      if (reported || recreatingRef.current || !gaveUpOnElementRef.current || ready || otherWay || isInAppBrowser()) {
        return;
      }

      reported = true;
      paymentLog.issue('stripe', 'payment_element_never_ready', {
        waited_ms: Date.now() - mountedAtRef.current,
        no_other_way_to_pay: true,
        stripe_js: stripeJsRef.current,
        ...(elementsAttempt > 0 && { elements_attempt: elementsAttempt }),
        ...(pageWasHiddenRef.current && { page_hidden: true }),
      });
    };

    window.addEventListener('pagehide', reportIfStranded);

    return () => {
      window.removeEventListener('pagehide', reportIfStranded);
      reportIfStranded();
    };
  }, []);

  // The notice tells either "choose another way" or "try again". Wallets can still come in after the
  // Element is given up on; once another way has been offered the notice keeps saying so, so its words
  // change at most once and never back.
  const showsUnavailableNotice = revealed && elementSettled && !elementReady;
  const offeredOtherWayRef = useRef(false);

  if (showsUnavailableNotice && hasOtherWayToPay) {
    offeredOtherWayRef.current = true;
  }

  const noticeOffersOtherWay = offeredOtherWayRef.current || hasOtherWayToPay;

  const billingCountry = [
    cached_order?.method === ORDER_METHODS.DELIVERY ? cached_order?.delivery_country : null,
    company?.country,
  ]
    .map((country) => (typeof country === 'string' ? country.toUpperCase() : null))
    .find((country) => country !== null && /^[A-Z]{2}$/.test(country));

  // Read once, at mount: the Element takes `layout` at creation only, and an `update` with a new
  // value would fold or unfold it under the customer as the selection moves. Opened unfolded, the
  // Element seats its first row itself and reports it through its change event — the card, or
  // whichever row comes first where the merchant hid the card — and the Pay button waits for that.
  const opensFoldedRef = useRef(isOwnRowSelected);

  const paymentElementOptions = useMemo<StripePaymentElementOptions>(
    () => ({
      layout: {
        type: 'accordion',
        radios: 'always',
        spacedAccordionItems: true,
        // No "More" button: every method the account can take is a row.
        visibleAccordionItemsCount: 0,
        // The rows this shop draws keep their logo at the end; the Element matches them.
        paymentMethodLogoPosition: 'end',
        // A row of this shop's own holds the selection: the Element opens with nothing lit.
        // Otherwise (nothing chosen yet, or a row of the Element's from a previous visit) it seats
        // its first row itself and says so through its change event.
        defaultCollapsed: opensFoldedRef.current,
      },
      // The Express Checkout Element shows the wallets, Link included: as a row here Link took the
      // whole Element over for a recognised customer and hid every other method behind its menu.
      // The native app mounts no Express Checkout Element, so there Link stays a row.
      wallets: {
        applePay: 'never',
        googlePay: 'never',
        link: isMobileApp && !hiddenWallets.includes('link') ? 'auto' : 'never',
      },
      // Stripe owns the address: it asks only what the method and the card's country need (nothing
      // extra for a German card, a postal code for a US one; since clover none for a UK one). The
      // shop prefills who is paying and never sends an address of its own.
      fields: { billingDetails: { email: 'auto', name: 'auto', phone: 'auto', address: 'auto' } },
      defaultValues: {
        billingDetails: {
          name: stripeFormat.name,
          email: stripeFormat.email,
          phone: billingPhoneNumber,
          // Without one Stripe guesses the card's country from the IP and the language (a Berlin
          // restaurant's card row came up "Spain"): the delivery address's, else the restaurant's.
          ...(billingCountry ? { address: { country: billingCountry } } : {}),
        },
      },
      // Saving a card is Stripe's own box (the CustomerSession on the Elements), so Stripe's own
      // terms go with it when it is ticked.
      paymentMethodOrder: ['card'],
    }),
    [isMobileApp, hiddenWallets, stripeFormat.name, stripeFormat.email, billingPhoneNumber, billingCountry],
  );

  return (
    <div className="relative" data-test="stripe-elements-payment-methods" data-revealed={revealed ? 'true' : 'false'}>
      {!revealed && <StripeElementsSkeleton filteredPaymentOpts={filteredPaymentOpts} />}

      <div
        className={classnames('flex flex-col gap-sm', revealed ? 'relative' : notYetInColumn)}
        aria-hidden={revealed ? undefined : true}
      >
        {!isMobileApp && (
          <ExpressCheckoutElements>
            <ExpressCheckout hiddenWallets={hiddenWallets} onSettled={onExpressSettled} />
          </ExpressCheckoutElements>
        )}
        {/* Shown before Stripe answered (the reveal timed out): the places stay drawn until it does. */}
        {revealed && !expressSettled && walletsSeen(company?.id) !== 0 && (
          <PaymentMethodsSkeleton
            rows={0}
            withWallets
            wallets={walletsSeen(company?.id) ?? 2}
            testId="stripe-express-checkout-skeleton"
          />
        )}

        {savedCard && (
          <PaymentMethod
            id={PaymentMethodEnum.Card}
            name={savedCard.name}
            extra={<PaymentMethodBrandIcons {...savedCard} className={rowIcon} />}
            isLastItem
            isSelected={isSavedCardSelected}
            isDisabled={disabled}
            onClick={() => {
              collapseElement();
              selectSavedCard();
            }}
            variant={Variant.StripeElements}
            key={`payment-method-option-saved-${PaymentMethodEnum.Card}`}
          />
        )}

        {nativeWalletOptions.map((item) => (
          <PaymentMethod
            id={item.payment_method_id}
            name={item.name}
            extra={<PaymentMethodBrandIcons {...item} className={rowIcon} />}
            isLastItem
            isSelected={selectedPaymentMethod === item.payment_method_id}
            isDisabled={disabled}
            onClick={() => selectRow(item.payment_method_id)}
            variant={Variant.StripeElements}
            key={`payment-method-option-${item.payment_method_id}`}
          />
        ))}

        {revealed && !elementSettled && (
          <PaymentMethodsSkeleton rows={ESTIMATED_ELEMENT_ROWS} testId="stripe-payment-element-skeleton" />
        )}
        {/* Given up on (never ready, or refused by Stripe): its places go, and the customer is told
            why the Element's methods are not among the rows left. */}
        {showsUnavailableNotice && (
          <div className={unavailableNotice} data-test="stripe-payment-element-unavailable" role="status">
            <AlertTriangle aria-hidden="true" className="h-[14px] w-[14px] shrink-0 mt-[2px]" />
            <div className="flex flex-col items-start gap-xs">
              <span>
                {tCoordinator(noticeOffersOtherWay ? 'payment_option_unavailable' : 'online_payment_unavailable')}
              </span>
              <button
                type="button"
                className={retryButton}
                disabled={disabled}
                onClick={() => {
                  recreatingRef.current = retryElements('manual');
                }}
                data-test="stripe-payment-element-retry"
              >
                <RotateCw aria-hidden="true" className="h-[14px] w-[14px]" />
                {tCoordinator('retry_payment_methods')}
              </button>
            </div>
          </div>
        )}
        <div
          className={classnames({
            'pointer-events-none opacity-60': disabled,
            [notYetInColumn]: !elementReady,
          })}
          data-test="stripe-payment-element"
          data-ready={elementReady ? 'true' : 'false'}
          data-selected={isElementSelected ? 'true' : 'false'}
        >
          {!elementLoadFailed && (
            <PaymentElement
              options={paymentElementOptions}
              onChange={onElementChange}
              onReady={() => {
                if (gaveUpOnElementRef.current) {
                  paymentLog.info('stripe', 'payment_element_ready_late', {
                    waited_ms: Date.now() - mountedAtRef.current,
                  });
                }

                setElementReady(true);
                setElementSettled(true);
              }}
              onLoadError={(event) => {
                setElementLoadFailed(true);
                setElementSettled(true);
                reportLoadError('payment_element', event.error);
              }}
            />
          )}
        </div>

        {Boolean(cashOption) && (
          <PaymentMethod
            id={PaymentMethodEnum.Cash}
            name={cashOption.name}
            extra={<PaymentMethodBrandIcons {...cashOption} className={brandIcon} />}
            isLastItem
            isSelected={selectedPaymentMethod === PaymentMethodEnum.Cash}
            isDisabled={disabled}
            onClick={() => selectRow(PaymentMethodEnum.Cash)}
            variant={Variant.StripeElements}
            key={`payment-method-option-${PaymentMethodEnum.Cash}`}
          />
        )}

        {Boolean(paypalOption) && offersPaypal(company, paypalVariant) && (
          // PayPal stays a platform charge (Stripe has no direct charges for it), outside the
          // Element and under the same collapsed heading the Adyen screen gives it.
          <Accordion
            disableGutters
            elevation={0}
            defaultExpanded={selectedPaymentMethod === PaymentMethodEnum.Paypal}
            className={paypalAccordion}
            // MUI's divider line above every accordion; the rows above it have none.
            sx={{ boxShadow: 'none', backgroundColor: 'transparent', '&::before': { display: 'none' } }}
          >
            <AccordionSummary
              expandIcon={<ChevronRight className="rotate-90 w-4 h-4" />}
              style={{ padding: 0, minHeight: 0 }}
              // One 12px step to the PayPal row below, as between the rows above.
              classes={{ content: '!m-none !pt-sm !pb-none text-sm font-medium text-brand-text-secondary' }}
              data-test="stripe-elements-more-methods"
            >
              {t('show_more_payment_methods')}
            </AccordionSummary>
            <AccordionDetails className="!p-none !pt-sm">
              <PaymentMethod
                id={PaymentMethodEnum.Paypal}
                name={paypalOption.name}
                extra={<PaymentMethodBrandIcons {...paypalOption} className={brandIcon} />}
                isLastItem
                isSelected={selectedPaymentMethod === PaymentMethodEnum.Paypal}
                isDisabled={disabled}
                onClick={() => selectRow(PaymentMethodEnum.Paypal)}
                variant={Variant.StripeElements}
                key={`payment-method-option-${PaymentMethodEnum.Paypal}`}
              />
            </AccordionDetails>
          </Accordion>
        )}
      </div>
    </div>
  );
}

/**
 * Apple Pay, Google Pay and Link on the restaurant's account. The wallet sheet opens on the
 * customer's tap; a ConfirmationToken is created from it before the order call, which closes it.
 * The backend reads the wallet from the token, runs its checks on it and confirms the intent with
 * it, and an OTP retry — a new order — carries the same token without a second sheet.
 */
function ExpressCheckout({
  hiddenWallets,
  onSettled,
}: {
  hiddenWallets: StripeWallet[];
  /** Whether any wallet button is shown. */
  onSettled: (available: boolean) => void;
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:Sections:PaymentInfoSection' });
  const { t: tPayButton } = useTranslation(undefined, { keyPrefix: 'Components:Payment:Buttons:PayButton' });
  const { t: tCoordinator } = useTranslation(undefined, { keyPrefix: 'Components:Payment:PaymentCoordinator' });

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const stripe = useStripe();
  const elements = useElements();
  const {
    isGiftCardCheckout,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    isSavedCardSelected,
    selectSavedCard,
    stripeElementType,
    selectStripeElementType,
    findSavedPaymentMethod,
    paymentOptions,
  } = usePaymentFlow();
  const savedCard = useMemo(() => findSavedPaymentMethod(PaymentMethodEnum.Card), [paymentOptions]);
  const {
    isPaymentProcessing,
    setIsPaymentProcessing,
    loading: paymentsLoading,
    basketTotal,
  } = usePaymentCoordinator();
  const { amount: elementsAmount, setWalletSheetOpen } = useStripeElementsSession();
  const { loading: draftLoading, isDraftSettling, isDraftCurrent } = useOrderFlow();
  const { isWithinThreshold, isWithinMaxThreshold } = useDeliveryThreshold();
  const { hasReachedCheckoutThreshold } = useCheckoutThreshold();
  const orderType = isGiftCardCheckout ? OrderType.GiftCard : OrderType.Default;

  // The checks the Pay button runs before it places an order, which a wallet tap would otherwise
  // skip: the order thresholds, a draft or the payment set-up still loading, a payment in flight.
  const blockedReason = useMemo<null | string>(() => {
    if (isPaymentProcessing) {
      return 'payment_in_progress';
    }

    if (paymentsLoading) {
      return 'payments_loading';
    }

    if (isGiftCardCheckout) {
      return null;
    }

    if (draftLoading) {
      return 'draft_loading';
    }

    if (!isWithinThreshold || !hasReachedCheckoutThreshold) {
      return 'below_threshold';
    }

    return null;
  }, [
    isPaymentProcessing,
    paymentsLoading,
    isGiftCardCheckout,
    draftLoading,
    isWithinThreshold,
    hasReachedCheckoutThreshold,
  ]);

  // One wallet payment at a time: a second confirm while an order is in flight (a sheet re-opened
  // over the OTP modal) would place a concurrent order.
  const confirmInFlightRef = useRef(false);

  const { generateReturnUrl } = useGenerateReturnUrl(orderType);
  const { checkout } = useCheckout(orderType, {
    loading: isPaymentProcessing,
    setLoading: setIsPaymentProcessing,
    onMobileAppCheckoutError: () => null,
  });
  const generateReturnUrlRef = useDeepRef<GenerateOrderReturnUrlFunction>(generateReturnUrl);
  const checkoutRef = useDeepRef<CheckoutFunction>(checkout);

  const [available, setAvailable] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const reportLoadError = useElementLoadErrorReport();

  // The wallets the element was created with, held from the tap until the sheet is closed and no
  // confirm is in flight. Stripe.js delivers the sheet's confirm to the element that opened it; a
  // new one in its place (a new key, or none for a zero amount) has no handler on the old one and
  // Stripe throws "Unhandled expressCheckout Element confirm event" (SHOP-WEB-45). What changed
  // meanwhile is applied once the hold is released.
  const [heldWallets, setHeldWallets] = useState<null | StripeWallet[]>(null);
  const holdsElement = heldWallets !== null;
  const mountedWallets = heldWallets ?? hiddenWallets;
  const mountedWalletsKey = mountedWallets.join(',');

  useEffect(() => {
    setWalletSheetOpen(holdsElement);
  }, [holdsElement]);

  useEffect(() => () => setWalletSheetOpen(false), []);

  const options = useMemo<StripeExpressCheckoutElementOptions>(
    () => ({
      paymentMethods: {
        // PayPal has its own row on the platform account; the other two never reach these restaurants.
        paypal: 'never',
        amazonPay: 'never',
        klarna: 'never',
        // 'always': desktop Chrome, Edge and Firefox offer Apple Pay through the iPhone QR hand-off
        // too, not Safari alone. Stripe still leaves it out where it cannot work (Android).
        applePay: mountedWallets.includes('apple_pay') ? 'never' : 'always',
        // 'always': shown where Google Pay can run even before the customer saved a card in it, so
        // it is not Link that sits next to Apple Pay by default.
        googlePay: mountedWallets.includes('google_pay') ? 'never' : 'always',
        link: mountedWallets.includes('link') ? 'never' : 'auto',
      },
      // The two platform wallets first; Link (which knows a returning customer's card) after them.
      paymentMethodOrder: ['apple_pay', 'google_pay', 'link'],
      buttonType: { applePay: 'order', googlePay: 'order' },
      buttonHeight: 48,
      layout: { maxColumns: 2, overflow: 'never' },
    }),
    [mountedWalletsKey],
  );

  // Settled, for the block's single reveal: ready with wallets or without, refused, or not drawn at all.
  const onReady = ({ availablePaymentMethods }: StripeExpressCheckoutElementReadyEvent) => {
    // A fresh element: whatever sheet the one before had open is gone with it.
    sheetOpenRef.current = false;
    setHeldWallets(null);
    dropWalletsNotAvailable(availablePaymentMethods);
    setAvailable(Boolean(availablePaymentMethods));
    rememberWalletsSeen(company?.id, Object.values(availablePaymentMethods ?? {}).filter(Boolean).length);
    onSettled(Boolean(availablePaymentMethods));
  };

  const hasAmount = basketTotal > 0;
  // Nothing to charge only once the amount is known: a refreshed or deep-linked payment screen has a
  // zero total until the draft comes back, and the wallets would then push the block down.
  const amountKnown = useAmountKnown();

  useEffect(() => {
    if (amountKnown && !hasAmount) {
      onSettled(false);
    }
  }, [amountKnown, hasAmount]);

  // What the customer is told when a tap is turned away: the Pay button's own words for an order
  // outside the thresholds, a "not ready yet" while the checkout is still loading. A payment already
  // in flight needs no word; its own spinner is on screen.
  const blockedMessage = (reason: string): null | string => {
    if (reason === 'below_threshold') {
      return isWithinMaxThreshold ? tPayButton('add_items') : tPayButton('remove_items');
    }

    return reason === 'payment_in_progress' ? null : tCoordinator('payment_not_ready');
  };

  // A tap on a wallet is choosing it: the selection moves to the wallet so the draft, the breakdown
  // (service fee, tip) and the Elements' amount follow it, as for any other row. What was selected
  // before is kept to go back to when the sheet is closed without paying, or the order fails — but
  // not after a refusal over the price (keepWallet).
  const previousSelectionRef = useRef<null | WalletTapPreviousSelection>(null);
  const selectedPaymentMethodRef = useDeepRef<PaymentMethodEnum | null>(selectedPaymentMethod);
  const stripeElementTypeRef = useDeepRef<string | null>(stripeElementType ?? null);
  // What the sheet showed when it opened, against what the order is now priced at on confirm.
  const sheetAmountRef = useRef<null | number>(null);
  // From the tap until the sheet is cancelled or gives up its token.
  const sheetOpenRef = useRef(false);
  // What the element last said it offers (null: nothing said yet), and whether a drop was held back
  // while a sheet or an order was open, to be looked at again once it is over.
  const lastAvailableRef = useRef<null | { methods: Partial<Record<string, boolean>> | undefined }>(null);
  const dropHeldRef = useRef(false);
  const [heldDropCheck, setHeldDropCheck] = useState(0);
  const pricingRef = useDeepRef({
    amount: elementsAmount,
    settling: Boolean(isDraftSettling || draftLoading),
    current: isDraftCurrent !== false,
  });

  const selectWallet = (wallet: StripeWallet) => {
    const method = paymentMethodFromStripeType(wallet === 'link' ? 'link' : 'card', wallet);

    if (!method || method === selectedPaymentMethod) {
      return;
    }

    // A second tap after a refused one finds the wallet already selected: the row to go back to is
    // still the one from before the first.
    if (!isExpressWalletMethod(selectedPaymentMethod)) {
      previousSelectionRef.current = {
        method: selectedPaymentMethod,
        isSavedCard: Boolean(isSavedCardSelected),
        elementType: stripeElementType ?? null,
      };
    }

    setSelectedPaymentMethod(method);
  };

  /**
   * A tap refused before any order (the wallet's draft not back in time, or priced differently from
   * the sheet) keeps the wallet: its draft is what the next tap's sheet must open on, and going back
   * to the row before would re-price to that row and refuse the next tap the same way. So nothing is
   * left to go back to (the sheet's cancel then changes nothing), and the Element folds, so no row of
   * its own looks chosen; the Pay button points to the wallet button (StripePayButton). Unless Link is
   * also the Element's own lit row (its Link row, then the Link button tapped): the selection is that
   * row's, the Pay button pays through it, and a folded Element would be submitted with nothing open.
   */
  const keepWallet = () => {
    previousSelectionRef.current = null;

    if (stripeElementTypeRef.current === 'link' && selectedPaymentMethodRef.current === PaymentMethodEnum.Link) {
      return;
    }

    elements?.getElement('payment')?.collapse();
  };

  /**
   * The selected wallet is no longer offered (hidden for the new order method, or gone from the
   * rebuilt Express Checkout Element): the Pay button would point to a button that is not there. The
   * selection goes back to the row from before the tap, or, after a refusal kept the wallet, to the
   * card on file, else to nothing, so the customer picks again.
   */
  const dropUnofferedWallet = (wallet: StripeWallet, reason: string) => {
    const selected = selectedPaymentMethodRef.current;

    if (
      !isExpressCheckoutWalletSelection(selected, stripeElementTypeRef.current) ||
      paymentMethodFromStripeType(wallet === 'link' ? 'link' : 'card', wallet) !== selected
    ) {
      return;
    }

    // The tap re-prices while the sheet is open (and again for an OTP retry), and Stripe can answer
    // that with the tapped wallet gone, or with no list at all. Going back to the row before now would
    // re-price the order under the sheet and refuse its confirm; it waits until the sheet or the order
    // is over.
    if (sheetOpenRef.current || confirmInFlightRef.current) {
      dropHeldRef.current = true;
      paymentLog.info('stripe', 'express_wallet_selection_drop_held', { company_id: company?.id, wallet, reason });

      return;
    }

    paymentLog.info('stripe', 'express_wallet_selection_dropped', { company_id: company?.id, wallet, reason });

    if (previousSelectionRef.current) {
      restorePreviousSelection();

      return;
    }

    if (savedCard) {
      selectSavedCard();
    } else {
      setSelectedPaymentMethod(null);
    }
  };

  useEffect(() => {
    hiddenWallets.forEach((wallet) => dropUnofferedWallet(wallet, 'hidden'));
  }, [hiddenWallets.join(',')]);

  const dropWalletsNotAvailable = (available: Partial<Record<string, boolean>> | undefined) => {
    lastAvailableRef.current = { methods: available };
    (Object.entries(EXPRESS_WALLET_KEYS) as [StripeWallet, string][]).forEach(([wallet, key]) => {
      if (!available?.[key]) {
        dropUnofferedWallet(wallet, 'unavailable');
      }
    });
  };

  // After the render that carries whatever the close itself chose, so a wallet already left behind
  // is not dropped a second time.
  const recheckHeldDrops = () => {
    if (dropHeldRef.current) {
      dropHeldRef.current = false;
      setHeldDropCheck((count) => count + 1);
    }
  };

  useEffect(() => {
    if (heldDropCheck === 0) {
      return;
    }

    hiddenWallets.forEach((wallet) => dropUnofferedWallet(wallet, 'hidden'));

    if (lastAvailableRef.current) {
      dropWalletsNotAvailable(lastAvailableRef.current.methods);
    }
  }, [heldDropCheck]);

  const restorePreviousSelection = () => {
    const previous = previousSelectionRef.current;
    previousSelectionRef.current = null;

    if (!previous || !isExpressWalletMethod(selectedPaymentMethodRef.current)) {
      return;
    }

    if (previous.isSavedCard) {
      selectSavedCard();
    } else if (previous.elementType && paymentMethodFromStripeType(previous.elementType) === previous.method) {
      // The Element's row: it was never folded, so it is still lit as it was.
      selectStripeElementType(previous.elementType);
    } else {
      setSelectedPaymentMethod(previous.method);
    }
  };

  /**
   * The wallet's own draft, asked for by the tap: back before the order is placed, so the order is
   * priced as the sheet was. A gift card has no draft; its amount is the basket's.
   */
  const waitForWalletPricing = async (): Promise<boolean> => {
    if (isGiftCardCheckout) {
      return true;
    }

    const startedAt = Date.now();

    while (pricingRef.current.settling && Date.now() - startedAt < WALLET_PRICING_WAIT_MS) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    return !pricingRef.current.settling && pricingRef.current.current;
  };

  const onClick = ({ resolve, reject, expressPaymentType }: StripeExpressCheckoutElementClickEvent) => {
    if (blockedReason !== null) {
      paymentLog.info('stripe', 'express_checkout_blocked', {
        company_id: company?.id,
        wallet: expressPaymentType,
        reason: blockedReason,
      });
      reject();

      const message = blockedMessage(blockedReason);

      if (message) {
        toast.error(message);
      }

      return;
    }

    // Synchronous, and resolved at once (Stripe gives the click a second): the sheet opens on the
    // Elements' amount as it stands, already the online price (see StripeCoordinator), and the
    // wallet's own draft runs meanwhile. The guest's name, email and phone come from the checkout
    // form, as for every other method.
    sheetAmountRef.current = elementsAmount;
    sheetOpenRef.current = true;
    setHeldWallets(mountedWallets);
    selectWallet(expressPaymentType as StripeWallet);
    resolve();
  };

  const onCancel = () => {
    sheetOpenRef.current = false;
    setHeldWallets(null);
    setIsPaymentProcessing(false);
    restorePreviousSelection();
    recheckHeldDrops();
  };

  const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    const wallet = event.expressPaymentType as StripeWallet;
    const type = wallet === 'link' ? 'link' : 'card';
    const paymentMethodId = paymentMethodFromStripeType(type, wallet);

    if (confirmInFlightRef.current) {
      paymentLog.info('stripe', 'express_checkout_confirm_ignored', { company_id: company?.id, wallet });
      event.paymentFailed({ reason: 'fail' });

      return;
    }

    confirmInFlightRef.current = true;
    paymentLog.info('stripe', 'express_checkout_confirm', { company_id: company?.id, wallet, order_type: orderType });

    // Anything short of a placed order goes back to the row the customer had before the tap, except
    // a refusal over the price, which keeps the wallet (and its price) for the next tap (keepWallet).
    // An OTP step-up holds the checkout's promise until the code is settled, so nothing goes back
    // while its modal is open.
    let placed = false;
    let keepsWallet = false;
    // Which call threw, for wallet_checkout_threw: Sentry's server-side scrubbing has eaten the
    // error message before (SHOP-WEB-29), so the step is what names the failing call.
    let step: 'pricing' | 'submit' | 'confirmation_token' | 'checkout' = 'pricing';

    try {
      if (!(await waitForWalletPricing())) {
        paymentLog.warn('stripe', 'express_checkout_pricing_not_ready', { company_id: company?.id, wallet });
        event.paymentFailed({ reason: 'fail' });
        toast.error(tCoordinator('payment_not_ready'));
        keepsWallet = true;

        return;
      }

      // The wallet's price differs from the one the sheet opened on (a method upcharge): never
      // charge what the customer did not approve. The Elements now carry the new amount, so the
      // next tap opens on it; the wallet stays selected, its price on the breakdown.
      if (sheetAmountRef.current !== null && sheetAmountRef.current !== pricingRef.current.amount) {
        paymentLog.info('stripe', 'express_checkout_amount_changed', {
          company_id: company?.id,
          wallet,
          sheet_amount: sheetAmountRef.current,
          amount: pricingRef.current.amount,
        });
        event.paymentFailed({ reason: 'fail' });
        toast.error(tCoordinator('wallet_total_changed'));
        keepsWallet = true;

        return;
      }

      step = 'submit';
      const { error: submitError } = await elements.submit();

      if (submitError) {
        paymentLog.warn('stripe', 'express_checkout_submit_failed', {
          company_id: company?.id,
          wallet,
          message: submitError.message,
        });
        // Stripe.js has already closed the sheet: a paymentFailed() now is one it rejects (SHOP-WEB-59).
        toast.error(submitError.message ?? tCoordinator('method_unavailable'));

        return;
      }

      // Stripe.js closes the sheet itself once the token is made — with its success mark, or its
      // error mark when this fails — so nothing after this point goes back to the sheet: a
      // paymentFailed() now is one Stripe.js rejects. The rest is told in the page.
      step = 'confirmation_token';
      const { error: tokenError, confirmationToken } = await stripe.createConfirmationToken({ elements });
      sheetOpenRef.current = false;

      if (tokenError) {
        paymentLog.error('stripe', 'wallet_confirm_failed', {
          company_id: company?.id,
          payment_method: paymentMethodId,
          error_type: tokenError.type,
          error_code: tokenError.code,
          message: tokenError.message,
        });
        toast.error(tokenError.message ?? tCoordinator('method_unavailable'));

        return;
      }

      // The backend confirms the intent with the token; a 402 means the customer has something
      // left to do, which for a wallet is a 3DS challenge at most.
      const confirmPayment: ConfirmPaymentFunction = async (order, paymentIntent) => {
        if (paymentIntent?.status !== 'requires_action') {
          paymentLog.issue('stripe', 'wallet_unexpected_intent', {
            order_uuid: order?.uuid,
            company_id: company?.id,
            payment_method: paymentMethodId,
            intent_status: paymentIntent?.status,
          });

          return { status: PaymentStatus.Failed, error: null, result: null };
        }

        const result = await handleStripeNextAction(stripe, paymentIntent);

        if (result.status === PaymentStatus.Failed) {
          paymentLog.error('stripe', 'wallet_confirm_failed', {
            order_uuid: order?.uuid,
            company_id: company?.id,
            payment_method: paymentMethodId,
            error_type: result.error?.type,
            error_code: result.error?.code,
            decline_code: result.error?.decline_code,
            message: result.error?.message,
          });

          return result;
        }

        paymentLog.info('stripe', 'wallet_confirm_succeeded', {
          order_uuid: order?.uuid,
          company_id: company?.id,
          payment_method: paymentMethodId,
        });

        return result;
      };

      // An OTP step-up retries with a new order and the same token: the sheet is long closed, and
      // the backend confirms whichever order passes.
      step = 'checkout';
      placed = await checkoutRef.current(confirmPayment, undefined, {
        // The backend reads the wallet from the token; the method here names the order for the
        // shop's own analytics and logs.
        stripe_confirmation_token: confirmationToken.id,
        payment_method_id: paymentMethodId,
        // Where a bank's page would send the customer back to; the backend fills in the order.
        return_url: generateReturnUrlRef.current({
          [PaymentReturnUrlPlaceholder.PaymentGateway]: PaymentGateway.Stripe,
        }),
        // A wallet's card is a device token, never one the customer agreed to keep.
        should_save_payment_info: false,
      });
    } catch (error) {
      paymentLog.issue('stripe', 'wallet_checkout_threw', {
        company_id: company?.id,
        payment_method: paymentMethodId,
        step,
        error_name: (error as any)?.name,
        // Stripe.js errors carry a type (IntegrationError and friends) next to the name.
        error_type: (error as any)?.type,
        message: (error as any)?.message,
      });
    } finally {
      confirmInFlightRef.current = false;
      setIsPaymentProcessing(false);

      if (keepsWallet) {
        // Kept for a second confirm from the same sheet, which must still match what it shows.
        keepWallet();
      } else {
        sheetAmountRef.current = null;
        sheetOpenRef.current = false;

        if (!placed) {
          restorePreviousSelection();
        }
      }

      if (!sheetOpenRef.current) {
        setHeldWallets(null);
      }

      if (placed) {
        dropHeldRef.current = false;
      } else if (!sheetOpenRef.current) {
        recheckHeldDrops();
      }
    }
  };

  // The element going away with a sheet open or a confirm in flight is how SHOP-WEB-45 starts: said,
  // so the next one names the path (a layout swap, the Elements re-created, the page left), and the
  // element is kept to answer that sheet. A layout effect, so this runs before react-stripe-js's own
  // cleanup destroys the element.
  const elementsRef = useDeepRef<null | StripeElements>(elements);

  useLayoutEffect(
    () => () => {
      if (!sheetOpenRef.current && !confirmInFlightRef.current) {
        return;
      }

      paymentLog.warn('stripe', 'express_checkout_unmounted_mid_sheet', {
        company_id: company?.id,
        confirm_in_flight: confirmInFlightRef.current,
      });

      const element = elementsRef.current?.getElement('expressCheckout');

      if (element) {
        retireExpressCheckoutElement(element, (event) =>
          paymentLog.warn('stripe', 'express_checkout_confirm_after_unmount', {
            company_id: company?.id,
            wallet: event.expressPaymentType,
          }),
        );
      }
    },
    [],
  );

  // Nothing to charge online (a free order): no wallet sheet for an amount the Elements were only
  // sized to so they could mount. Not while a sheet holds the element: a re-price under it waits.
  if ((!hasAmount && !holdsElement) || loadFailed) {
    return null;
  }

  return (
    <div
      className={classnames({ hidden: !available })}
      data-test="stripe-express-checkout"
      data-ready={available ? 'true' : 'false'}
    >
      <ExpressCheckoutElement
        // `paymentMethods` is read at creation only: a wallet the merchant hides for the new order
        // method needs a fresh element.
        key={mountedWalletsKey || 'all-wallets'}
        options={options}
        onReady={onReady}
        onLoadError={(event) => {
          setLoadFailed(true);
          onSettled(false);
          reportLoadError('express_checkout', event.error);
        }}
        onAvailablePaymentMethodsChange={({ paymentMethods }) => {
          // No list means no button renders (as onReady reads it); a drop is still held while a
          // sheet or an order is open.
          dropWalletsNotAvailable(
            paymentMethods
              ? Object.fromEntries(
                  Object.entries(paymentMethods).map(([key, value]) => [key, Boolean(value?.available)]),
                )
              : undefined,
          );
        }}
        onClick={onClick}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
      <div className={divider}>
        <span className={dividerLine} />
        <span className="text-xs uppercase text-muted-foreground">{t('express_checkout_divider')}</span>
        <span className={dividerLine} />
      </div>
    </div>
  );
}

/** How long a wallet's confirm waits for the draft its tap asked for. */
export const WALLET_PRICING_WAIT_MS = 5_000;

type WalletTapPreviousSelection = {
  method: PaymentMethodEnum | null;
  isSavedCard: boolean;
  elementType: string | null;
};

const isExpressWalletMethod = (method: PaymentMethodEnum | null | undefined): boolean =>
  method === PaymentMethodEnum.ApplePay || method === PaymentMethodEnum.GooglePay || method === PaymentMethodEnum.Link;

/** A wallet of the Express Checkout Element's holds the selection: Link only when it is not the Element's own row. */
const isExpressCheckoutWalletSelection = (
  method: PaymentMethodEnum | null | undefined,
  elementType: string | null | undefined,
): boolean =>
  method === PaymentMethodEnum.ApplePay ||
  method === PaymentMethodEnum.GooglePay ||
  (method === PaymentMethodEnum.Link && elementType !== 'link');

/** The Express Checkout Element's names for the wallets it reports as available. */
const EXPRESS_WALLET_KEYS: Record<StripeWallet, string> = {
  apple_pay: 'applePay',
  google_pay: 'googlePay',
  link: 'link',
};

/**
 * An Element Stripe could not load is logged as the failure it is; with a CustomerSession in use the
 * session is dropped and the Elements come back without it, a guest's, since the save box is optional
 * and must never take the checkout down.
 */
function useElementLoadErrorReport() {
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { withCustomerSession, dropCustomerSession } = useStripeElementsSession();

  return (element: 'payment_element' | 'express_checkout', error: undefined | StripeError) => {
    paymentLog.error('stripe', `${element}_load_failed`, {
      company_id: company?.id,
      with_customer_session: withCustomerSession,
      error_type: error?.type,
      error_code: error?.code,
      message: error?.message,
    });

    if (withCustomerSession && element === 'payment_element') {
      dropCustomerSession();
    }
  };
}

/**
 * The wallets get an Elements group of their own: `elements.submit()` validates every Element in its
 * group, so a card half typed into the Payment Element failed an Apple Pay confirm (SHOP-WEB-59). Same
 * Stripe instance and options, updated with them, but no CustomerSession: only the Payment Element's
 * save box reads it.
 */
function ExpressCheckoutElements({ children }: PropsWithChildren) {
  const stripe = useStripe();
  const { elementsOptions } = useStripeElementsSession();
  const options = useMemo(() => {
    if (!elementsOptions) {
      return null;
    }

    const { customerSessionClientSecret: _customerSession, ...rest } = elementsOptions;

    return rest;
  }, [elementsOptions]);

  if (!options) {
    return null;
  }

  return (
    <Elements stripe={stripe} options={options}>
      {children}
    </Elements>
  );
}

// Every icon at the end of a row sits in the same 16px box Stripe gives its method logos…
const rowIcon = 'h-4 w-auto my-auto';
// …except the cash and PayPal marks, which keep the colour and size the rest of the shop draws them in.
const brandIcon = 'h-[18px] w-auto my-auto';
const paypalAccordion = '!rounded-none !bg-transparent';
// Laid out (so Stripe can draw it and report ready) but out of the column and unseen.
const notYetInColumn = 'invisible absolute inset-x-0 top-0';
const divider = 'flex items-center gap-sm pt-sm';
const unavailableNotice = 'flex items-start gap-xs text-[13px] leading-[18px] text-brand-text-secondary';
const retryButton =
  'inline-flex items-center gap-2xs font-semibold text-brand-text-primary underline underline-offset-2 disabled:opacity-60';
const dividerLine = 'h-px flex-1 bg-gray dark:bg-brand-border';
